/** Formatação dos números do relatório. */

const nf = new Intl.NumberFormat('pt-BR');

/**
 * Campo ausente vira "—", não zero.
 *
 * Um backend mais antigo que não conhece a métrica devolve `undefined`, e
 * renderizar isso como `US$ 0,00` faz falta de dado parecer resultado real —
 * foi o que fez a receita vista aparecer zerada com 50% de viewability. Zero
 * de verdade continua sendo zero.
 */
const ausente = (value: number | null | undefined): boolean =>
    value === null || value === undefined || Number.isNaN(value);

export const num = (value: number | null | undefined): string =>
    ausente(value) ? '—' : nf.format(Math.round(value as number));

export const pct = (value: number | null | undefined): string =>
    ausente(value) ? '—' : `${(value as number).toFixed(1)}%`;

export const money = (value: number | null | undefined): string =>
    ausente(value)
        ? '—'
        : (value as number).toLocaleString('pt-BR', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 2,
          });

/** Preço de step: sem casas quando é inteiro, duas quando não. */
export const price = (value: number): string =>
    Number.isInteger(value) ? String(value) : (value ?? 0).toFixed(2);

export const decimal = (value: number | null | undefined, digits = 1): string =>
    ausente(value) ? '—' : (value as number).toFixed(digits);

/**
 * Data pelo calendário local, não por `toISOString()`.
 *
 * `toISOString` converte para UTC antes de formatar. Em Brasília isso empurra
 * tudo que acontece a partir das 21h para o dia seguinte: quem abrisse o
 * relatório às 22h e clicasse em "Ontem" recebia hoje.
 */
const iso = (d: Date): string =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
        d.getDate(),
    ).padStart(2, '0')}`;

const somaDias = (dias: number): Date => {
    const d = new Date();
    d.setDate(d.getDate() + dias);
    return d;
};

export interface Periodo {
    id: string;
    label: string;
    range: () => { start: string; end: string };
}

/**
 * Ontem é um dia só, não uma janela terminando hoje — por isso não sai de uma
 * função de "últimos N". Os últimos 3 e 7 incluem hoje, que é o que a faixa de
 * tempo real já mostra em separado.
 */
export const PERIODOS: Periodo[] = [
    {
        id: 'ontem',
        label: 'Ontem',
        range: () => ({ start: iso(somaDias(-1)), end: iso(somaDias(-1)) }),
    },
    {
        id: '3d',
        label: 'Últimos 3 dias',
        range: () => ({ start: iso(somaDias(-2)), end: iso(new Date()) }),
    },
    {
        id: '7d',
        label: 'Últimos 7 dias',
        range: () => ({ start: iso(somaDias(-6)), end: iso(new Date()) }),
    },
];

export const SOURCE_LABEL: Record<string, string> = {
    structure: 'Estrutura automática',
    manual: 'Granulação manual',
    default: 'Steps fixos',
};

/**
 * Preenchimento em faixas. O verde não é "bom" no absoluto — é relativo ao que
 * uma cascata costuma entregar; serve para achar o outlier na tabela.
 */
export const fillTone = (rate: number): string => {
    if (rate >= 60) return 'text-emerald-600';
    if (rate >= 25) return 'text-amber-600';
    return 'text-red-600';
};
